/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useEffect, useMemo, useState } from 'react'
import StoreService from '../../../common/redux/store.service'
import { Provider, useDispatch, useSelector } from 'react-redux'
import { FieldConfig, DataGroupsOverview as Overview } from 'tinybots-react-components'
import { OverviewTypes } from '../types/types'
import { AnalyticsState, dataGroupSelector, fetchDataGroupOverview, fetchOrganisations } from '../redux/reducer'
import { RootState } from '../../../common/redux/root.reducer'
import { AppDispatch } from '../../../common/redux/store'
import { DetailedRelation } from '../../overview/types/types'
import styled from 'styled-components'

const OverviewWrapper = styled.div`
  height: 100%;
  overflow-y: auto;
`

export interface DataGroupOverviewProps {
  $http: any
  StoreService: StoreService
}

const organisationalSelectedFields: FieldConfig[] = [
  {
    key: 'title',
    displayName: 'Relation',
    type: 'string'
  }
]

const personalSelectedFields: FieldConfig[] = [
  {
    key: 'title',
    displayName: 'Title',
    type: 'string'
  },
  {
    key: 'updatedAt',
    displayName: 'Updated on',
    type: 'date'
  }
]

const getQueryParam = (key: string): string | null =>
  new URLSearchParams(window.location.search).get(key)

const ConnectedDataGroupOverview = (props: DataGroupOverviewProps) => {
  const dispatch: AppDispatch = useDispatch()

  const [view, setView] = useState<OverviewTypes>(getQueryParam('view') === 'organisational' ? 'organisational' : 'personal')

  const { organisations, dataGroupOverview: MyDataGroupsOverview } = useSelector<RootState, AnalyticsState>(dataGroupSelector)

  useEffect(() => {
    dispatch(fetchOrganisations())
    dispatch(fetchDataGroupOverview())
  }, [])

  const organisationalDataGroups = useMemo(() => (
    organisations.map((org: DetailedRelation) => ({
      id: org.id,
      title: org.name
    }))
  ), [organisations])

  useEffect(() => {
    if (view === 'personal') {
      setData(MyDataGroupsOverview)
    } else {
      setData(prev =>
        JSON.stringify(prev) !== JSON.stringify(organisationalDataGroups)
          ? organisationalDataGroups
          : prev
      )
    }
  }, [view, organisationalDataGroups, MyDataGroupsOverview])

  const [data, setData] = useState(
    view === 'personal' ? MyDataGroupsOverview : organisationalDataGroups
  )

  const handleOnViewChange = (newView: OverviewTypes) => {
    setView(newView)
    setData(newView === 'personal' ? MyDataGroupsOverview : organisationalDataGroups)

    const params = new URLSearchParams(window.location.search)
    params.set('view', newView)
    const newUrl = `${window.location.pathname}?${params.toString()}`
    window.history.replaceState({}, '', newUrl)
  }

  const handleOnSearch = (searchText: string) => {
    const sourceData = view === 'personal' ? MyDataGroupsOverview : organisationalDataGroups
    const filteredData = sourceData.filter(item =>
      item.title.toLowerCase().includes(searchText.toLowerCase())
    )
    setData(filteredData)
  }

  const handleOnAnalyticsClick = () => {
    window.location.href = '/custom-query-page'
  }

  const handleOnTableRowClick = (id: number) => {
    if (view === 'personal') {
      window.location.href = `/data-group?data-group-id=${id}&data-block-id=1&type=personal`
    } else {
      window.location.href = `/data-group?relation-id=${id}&data-block-id=1&type=organisational`
    }
  }

  return (
    <OverviewWrapper>
      <Overview onTableRowClick={handleOnTableRowClick} selectedFields={view === 'personal' ? personalSelectedFields : organisationalSelectedFields} activeView={view} onViewChange={handleOnViewChange} onSearch={handleOnSearch} onAnalyticsGraphQlViewClick={handleOnAnalyticsClick} data={data} />
    </OverviewWrapper>
  )
}

const DataGroupOverview: React.FC<DataGroupOverviewProps> = ({ StoreService, $http }) => (
  <Provider store={StoreService.store}>
    <ConnectedDataGroupOverview $http={$http} StoreService={StoreService} />
  </Provider>
)

export default DataGroupOverview
